import React from "react";
import GuideLayout from "../../components/layout/GuideLayout";
import Section from "../../components/layout/Section";
import Subheader from "../../components/layout/Subheader";
import {
  Typography,
  Link,
  Box,
} from "@mui/material";
import BulletList from "../../components/layout/BulletList";

const reasons = [
  "It's a (very) old item",
  "It was a direct buyable that not many paid attention to",
  "It was not available for very long, so very few were around to get it",
  "Not many people opened the capsule/played the event it was in",
  "Item has never been re-released"
];

const sections = [
  { id: "top", title: "Hunting Down Hard to Find Items" },
  { id: "whats-htf", title: "What's HTF?" },
  { id: "trade-post", title: "Making a Trade Post", },
  { id: "coldmailing", title: "Coldmailing", },
  { id: "three-way-trades", title: "Three Way Trades" },
  { id: "overoffering", title: "Overoffering" },
  { id: "final-words", title: "Final Words" },
];

const HTFItemGuide = () => {
  return (
    <GuideLayout sections={sections}>
      <Section id="top" title="Hunting Down Hard to Find Items">
        <Typography
          variant="caption"
          color="secondary"
          sx={{ mt: -1, mb: 2, display: "block" }}
        >
          Last updated: September 2025
        </Typography>
        <Typography paragraph>
          With thousands of Neocash items in the game, there's bound to be many items that are rare and hard to find. Maybe you were browsing and you found the perfect item for your pet - great! Now you want to trade for it and there's an insanely low number of people who have it up for trade - not so great. Let's talk all about hard to find items and some ways to hunt them down.
        </Typography>
        <Subheader title={"Setting Expectations"} />

        <Typography paragraph>
          Hard to find (HTF) items are exactly that - hard to find. A lot of this may depend on luck to stumble upon the right trader. Hopefully this guide can give you new ideas to try if you're looking for an item, but there is no guarantee that you will find a trade by trying these methods. Hunting down HTF items is sometimes a whole journey in itself, so set your expectations on the lower end and be patient!
        </Typography>
      </Section>

      <Section id="whats-htf" title="What's HTF?">
        <Typography paragraph>
          When we say HTF item, we're talking about the items with very few users (usually less than 30) having it up for trade. These items are hard to find because there's not many people who publicly list it up for trade, and sometimes, the users who have it might not respond or might not even be active players anymore.
        </Typography>

        <Subheader title="Why would something have such a low offering?" />
        With items that have low offering, it's typically for one or more of these reasons
        <BulletList items={reasons} />

        <Subheader title="What would be a fair offer?" />
        <Typography paragraph>
          There is no standard rule for values for HTF items because values are affected by a lot of other factors. We have a <Link href="/articles/values">whole article on values</Link> if you'd like to go in depth, but generally we recommend checking past trade reports to see what others have valued it previously. If there are no trade reports, you can always make a post to ask others for a value check to gather other opinions.
        </Typography>

        <Typography paragraph>
          A low offering does not inherently mean it's a high value item. Some HTF items are valued high, but for a lot of obscure items, there's a low offering and sometimes even lower seeking. It may indicate the item is rare, but if no one wants it, it would be really difficult to trade it for some obscenely high value.
        </Typography>

        <Typography paragraph>
          Ultimately, the value of the item will be whatever you and the trader are comfortable trading at. You can always ask someone what they'd value the item at too! Saying something like "Hi! I'm interested in your ITEM - what do you value it at?" would do the trick.
        </Typography>
      </Section>

      <Section id="trade-post" title="Making a Trade Post">
        <Typography paragraph>
          When looking for an item, go ahead and post in NC Trading! When you're looking for something hard to find, a large part of the battle is catching the attention of someone who owns it. Consider including the item name in your title! "Seeking: SPECIFIC ITEM" is more likely to get someone who owns the item to click vs a more generic board title like "Seeking: Wishlist Offering: Tradelist". Bonus: Add a few emoticons in your title to be eye-catching!
        </Typography>

        <Typography paragraph>
          Alternatively, if you're getting really desperate, you could make a board title like "Can you check if you have this item hidden in your SDB" and give more details in the post. This works best if the item you're looking for is really obscure, like an old neohome item or some rare capsule because some users might not even be aware they own one.
        </Typography>

        <Typography paragraph>
          When it comes to HTF items, it's normal to not get any responses - it's just part of the search! Bump your board periodically for visibility or perhaps try again on another day/time.
        </Typography>

      </Section>

      <Section id="coldmailing" title="Coldmailing">
        <Typography paragraph>
          Coldmailing is a popular strategy but can often feel tedious because everything is asynchronous. For wearable items, many people go on DTI's offering page and start neomailing those who have the item up for trade. If your item is a nonwearable or if you have already exhausted the DTI offering lists, try JN's offering list.
        </Typography>

        <Typography paragraph>
          In the best case, you'll find someone who has it UFT and they'd trade with you for something you have from their wishlist. If there's any lists like that, neomail them first since they're most likely to trade.
        </Typography>

        <Typography paragraph>
          When you come across someone else's tradelist, try your best to read their notes if they have any! For example, if they're not interested in browsing tradelists and you don't have any of their wishlist items, it's probably for the better to skip over them. If there are no notes, then asking never hurts!
        </Typography>

        <Typography paragraph>
          If you're neomailing someone for the first time, don't overthink it! You can simply say something like “Hi! I'm interested in your X Item listed on DTI. Would you trade it for X item on your wishlist? / Are you open to browsing tradelists? (and link) / Are you open to trading it for GBCs/custom/etc?” - use whatever applies to your case!
        </Typography>

        <Typography paragraph>
          Coldmailing can get frustrating because most of the time, people do not respond. It's not always the case that someone ignores you on purpose though; some people just don't log on as frequently and/or mails just get missed sometimes. If this happens to you, don't let it discourage you too much! It's (unfortunately) a common occurrence and a shared sentiment among traders.
        </Typography>

        <Subheader title="Should I send a batch of neomails or one by one?" />
        <Typography paragraph>
          People have their own mailing strategies and there's not really a right or wrong way. Some like to mail an offer and wait a certain amount of time (usually 24hrs) before mailing the next person so they never accidentally get two accepted offers. Some like to mail a bunch at once because they don't expect replies.
        </Typography>

        <Typography paragraph>
          Some traders will find it frustrating to get an offer, accept it, and then suddenly the person says they found a different trade already. Others might argue that it's fine as long as the person contacts them and doesn't ghost them. We just recommend being mindful and consider how you'd like to be treated if you were on the other end of the interaction.
        </Typography>
      </Section>


      <Section id="three-way-trades" title="Three Way Trades">
        <Typography paragraph>
          For example, let's say Trader A has something you want, but they will only trade it for 5 GBCs which you have none of at the moment. You can ask them if they're willing to wait until you obtain the 5 GBCs they want. If they agree, then you can look for trades with other people and have them send the GBCs directly to Trader A. Once your end of the Trade is fulfilled, Trader A sends the item you really wanted, and both ends can accept the items from the transfer log.
        </Typography>

        <Typography paragraph>
          This applies to items too. If the item you're looking for is really HTF, working out a three way trade might be easier if the person that has your item wants other things that are easier to find. It may be a hassle and requires some patience on both ends so not everyone will want to do this, but there's no harm in asking!
        </Typography>
      </Section>


      <Section id="understanding-item" title="Understanding the Item">

        <Typography paragraph>
          We believe it can be helpful to understand where an item comes from in case there are other ways to obtain it. If you're struggling to trade for a HTF item, chances are you probably want to try anything you can.
        </Typography>

        <Typography paragraph>
          We recommend looking the item up on something like Jellyneo to get a better understanding of where the item came from. Let's use the item Ringmaster Coat for example and pretend that we are seeking it. At the time of writing this guide, the ratio is <Link href="https://impress.openneo.net/items/64088/trades/offering">12 offering / 11 seeking on DTI</Link> and trades for <Link href="https://stylisher.club/search/Ringmaster%20Coat">about 3 based on past reports</Link>. That's an extremely low offering compared to more recent items, so let's dig deeper.
        </Typography>

        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/htf-items/ringmaster_coat_jn.png')} alt="ringmaster coat on jellyneo" style={{ width: '600px', maxWidth: "80%", borderRadius: 5 }} />
        </Box>

        <Typography paragraph>
          On the <Link href="https://items.jellyneo.net/item/50919/">JN page</Link>, you can see that it was available from 2012-2014, which is over a decade ago.
        </Typography>

        <Typography paragraph>
          In this case, it's also in the description, but you can also see from the “Available From” section that this coat can be found in other items. Scrolling down, we can see specifically that it was from the Big Top NC Fortune Cookie, and we can also click on that item page to get further context.
        </Typography>

        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/htf-items/ringmaster_coat_jn2.png')} alt="ringmaster coat on jellyneo available from section" style={{ width: '600px', maxWidth: "80%", borderRadius: 5 }} />
        </Box>
        <Typography paragraph>
          Looking at these pages can often give insight on why it might be so hard to find and now we know why this coat might be HTF - it's an old item, it's never been rereleased, and it's from a limited time fortune cookie. However, because it is from a fortune cookie, another option has appeared: If you can't find the coat specifically, maybe you can obtain it from the original item it came from! There's users who have the Big Top NC Fortune Cookie up for trade, and that's more users to try.
        </Typography>

        <Typography paragraph>
          Note: Be sure to read up on the items you trade for! In this specific case, you'd want to read up on how NC fortune cookies work and their values before trading for one if you are unfamiliar with them. You wouldn't want to trade for something expecting to guarantee your HTF item when it doesn't!
        </Typography>

      </Section>

      <Section id="overoffering" title="Overoffering">
        <Typography paragraph>
          It's not necessarily bad to overoffer - the important thing is that you are aware of it and you are still happy with the trade! If you really want an item, overoffering might be the way to go. Many consider it a last resort, though sometimes, desperate times call for desperate measures. Some people will not trade off their HTF item because it would be really hard to replace, but sometimes, people budge when there's a deal they can't pass up!
        </Typography>

        <Typography paragraph>
          If you are trying to make an overoffer, you can start by offering a cap or two above what the item usually trades for. To make offers more enticing, you may want to consider offering more popular items if you have any, or offer BF/GBCs and custom. Depending on what's in currently in the mall, offering custom could be very popular!
        </Typography>

      </Section>


      <Section id="final-words" title="Final Words">
        <Typography paragraph>
          Kinda time to plug that if you trade for HTF item, we'd really appreciate it if you report your trade! HTF items don't get traded often since they are hard to find, so in many cases, the data we have is outdated or doesn't exist. More recent data will only help the NC trading community. Best of luck hunting down those HTF items and happy trading :)
        </Typography>
      </Section>

    </GuideLayout>
  );
};

export default HTFItemGuide;
